import React from 'react';
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogFooter,
  TextArea,
  Button,
  Flex,
  Text,
} from '@chatsdet/ui';

type DeactivateNodeDialogProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** true = deactivating the node; false = reactivating it. */
  next: boolean;
  /** Human-readable label of the node being toggled, e.g. "Click Submit". */
  nodeLabel: string;
  onConfirm: (reason: string) => void;
};

/**
 * Confirms deactivating/reactivating a Flow Designer node and captures an
 * optional reason, recorded as a `workflow_events` audit row via
 * `POST /workflows/:id/nodes/:nodeId/deactivate|activate` (see
 * `useSetNodeDeactivated`). Mirrors the structure of VersionNameDialog.
 *
 * Closes as soon as it is confirmed rather than waiting on the mutation: the
 * canvas updates optimistically and a failure rolls back with a toast, which
 * is how this toggle behaved before it grew a dialog.
 */
export function DeactivateNodeDialog({
  isOpen,
  onOpenChange,
  next,
  nodeLabel,
  onConfirm,
}: DeactivateNodeDialogProps) {
  const [reason, setReason] = React.useState('');

  React.useEffect(() => {
    if (isOpen) setReason('');
  }, [isOpen]);

  const title = next ? 'Deactivate step' : 'Reactivate step';
  const confirmLabel = next ? 'Deactivate' : 'Reactivate';

  const handleSubmit = () => {
    onConfirm(reason);
    onOpenChange(false);
  };

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange}>
      <span />
      <Dialog title={title}>
        <DialogContent>
          <Flex direction="column" gap="md">
            <Text className="text-sm text-muted-foreground">
              {next ? 'Deactivating' : 'Reactivating'}{' '}
              <strong className="font-semibold text-foreground">{nodeLabel}</strong>.
            </Text>
            <TextArea
              label="Reason (optional)"
              placeholder="Why are you making this change?"
              value={reason}
              onChange={setReason}
              rows={3}
              autoFocus
            />
            <Text className="text-xs text-muted-foreground">
              This change (and your reason, if you add one) shows up in the flow's Comments tab
              timeline.
            </Text>
          </Flex>
        </DialogContent>
        <DialogFooter>
          <Button variant="outline" onPress={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" onPress={handleSubmit}>
            {confirmLabel}
          </Button>
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );
}
